import type { TellescopeMethodQueryFilterConfig, TellescopeUpdaterFn } from "../../hooks";
import type { ModelName, QueryMethodForModel } from "../../types";
import { getObjectEntries } from "../../utils";

type FilterMutationSideEffectUpdatersWithoutUpdaterReturn<Model extends ModelName> = {
  methodConfigs: Partial<{
    [QMethod in QueryMethodForModel<Model>]: TellescopeMethodQueryFilterConfig<Model, QMethod> & {
      updater: TellescopeUpdaterFn<Model, QMethod>;
    };
  }>;
  otherModelConfigs?: {
    [OModel in Exclude<ModelName, Model>]?: Partial<{
      [QMethod in QueryMethodForModel<OModel>]: TellescopeMethodQueryFilterConfig<OModel, QMethod> & {
        updater?: TellescopeUpdaterFn<OModel, QMethod>;
      };
    }>;
  };
};

/**
 * Filters out a configured side effect config from AUTOMATIC_MUTATION_SIDE_EFFECT_CONFIGS for a given model and mutation
 * method that do not have a specified updater.
 *
 * @template Model - The Tellescope model name (e.g., "users", "appointments")
 * @template Method - The mutation method for the model (e.g., "create_user", "update_appointment")
 *
 * @param configs - Configurations for the model/method to be filtered
 */
export const filterMutationSideEffectUpdatersWithoutUpdater = <Model extends ModelName>(
  config: Partial<{
    [QMethod in QueryMethodForModel<Model>]: TellescopeMethodQueryFilterConfig<Model, QMethod> & {
      updater?: TellescopeUpdaterFn<Model, QMethod>;
    };
  }> & {
    _otherModels?: {
      [OModel in Exclude<ModelName, Model>]?: Partial<{
        [QMethod in QueryMethodForModel<OModel>]: TellescopeMethodQueryFilterConfig<OModel, QMethod> & {
          updater?: TellescopeUpdaterFn<OModel, QMethod>;
        };
      }>;
    };
  } = {}
): FilterMutationSideEffectUpdatersWithoutUpdaterReturn<Model> => {
  const entries = getObjectEntries(config);
  return entries.reduce<FilterMutationSideEffectUpdatersWithoutUpdaterReturn<Model>>(
    (acc, [key, value]) => {
      if (key === "_otherModels" && !!value) {
        const otherModelEntries = Object.entries(value);
        const otherModelsConfig = Object.fromEntries(
          otherModelEntries.reduce<typeof otherModelEntries>((acc, [model, methodConfig]) => {
            const filteredMethodConfig = filterMutationSideEffectUpdatersWithoutUpdater<ModelName>(methodConfig as any);
            if (Object.values(filteredMethodConfig.methodConfigs as Record<string, unknown>).length) {
              acc.push([model, filteredMethodConfig.methodConfigs]);
            }
            return acc;
          }, [])
        );
        if (Object.values(otherModelsConfig).length) {
          // @ts-expect-error type is too broad but runtime is safe
          acc.otherModelConfigs = otherModelsConfig;
        }
        return acc;
      }

      if (!value || !value?.updater) {
        return acc;
      }
      // @ts-expect-error TS doesn't know we've filtered out values without an updater
      acc.methodConfigs[key] = value;
      return acc;
    },
    { methodConfigs: {} }
  );
};
